"use client";

import {
    joinCommunityWaitlist,
    getCommunityWaitlistSignupsInfo,
    GetCommunity,
} from "../actions";
import { CommunityAvatar } from "~/components/v4/shadcn/CommunityAvatar";
import { Button } from "~/components/v4/themed/Button";
import { useState, useEffect, useTransition } from "react";
import LoginModal from "~/components/v3/themed/LoginModal";
import { useSession } from "next-auth/react";
import { toast } from "react-toastify";
import { FaFacebook, FaInstagram, FaLinkedin, FaTiktok } from "react-icons/fa";
import Modal from "~/components/v3/themed/Modal";
import { LuCopy } from "react-icons/lu";
import { FaThreads, FaXTwitter } from "react-icons/fa6";
import { APP_URL } from "~/utils/constants";
import { useQuery } from "@tanstack/react-query";

type Props = {
    community: GetCommunity;
    isLoggedIn: boolean;
};

const WaitlistCommunity = ({ community, isLoggedIn }: Props) => {
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [openInMode, setOpenInMode] = useState<
        "login" | "signup" | undefined
    >();
    const [isPending, startTransition] = useTransition();
    const { update } = useSession();

    // Use React Query to fetch waitlist data
    const { data: waitlistData, refetch } = useQuery({
        queryKey: ["communityWaitlist", community.id],
        queryFn: () => getCommunityWaitlistSignupsInfo(community.id),
        initialData: {
            isCurrentUserOnCommunityWaitlist: false,
            waitlistSignupsCount: 0,
        },
    });

    // Initialize state with query data
    const [isJoined, setIsJoined] = useState(
        waitlistData.isCurrentUserOnCommunityWaitlist,
    );
    const [waitlistCount, setWaitlistCount] = useState(
        waitlistData.waitlistSignupsCount,
    );

    // Update local state when query data changes
    useEffect(() => {
        setIsJoined(waitlistData.isCurrentUserOnCommunityWaitlist);
        setWaitlistCount(waitlistData.waitlistSignupsCount);
    }, [waitlistData]);

    const handleJoinWaitlist = () => {
        // Optimistic update - increment count and set joined state immediately
        if (!isJoined) {
            setWaitlistCount((prevCount) => prevCount + 1);
            setIsJoined(true);
        }

        startTransition(async () => {
            toast.loading("Joining waitlist...", { toastId: "join-waitlist" });
            const result = await joinCommunityWaitlist(community.id);

            if (result.success) {
                toast.update("join-waitlist", {
                    render: "Successfully joined waitlist!",
                    type: "success",
                    isLoading: false,
                    autoClose: 2000,
                });
                // Show share modal after successful join
                setIsShareModalOpen(true);
                // Refresh session and query data to update UI state
                await update();
                await refetch();
            } else {
                // Revert optimistic updates if the operation failed
                setIsJoined(waitlistData.isCurrentUserOnCommunityWaitlist);
                setWaitlistCount(waitlistData.waitlistSignupsCount);

                toast.update("join-waitlist", {
                    render: result.message || "Failed to join waitlist",
                    type: "error",
                    isLoading: false,
                    autoClose: 2000,
                });
            }
        });
    };

    const handleShare = () => {
        setIsShareModalOpen(true);
    };

    // Generate waitlist URL for sharing
    const waitlistUrl = `${APP_URL}/${community.slug}`;

    // Handle copy to clipboard
    const handleCopyLink = () => {
        navigator.clipboard
            .writeText(waitlistUrl)
            .then(() => {
                toast.success("Link copied to clipboard!", {
                    autoClose: 1500,
                });
            })
            .catch(() => {
                toast.error("Failed to copy link", {
                    autoClose: 1500,
                });
            });
    };

    // Handle social media sharing
    const handleSocialShare = (platform: string) => {
        let shareUrl = "";
        const shareText = `I'm building momentum for ${community.name} community events. Once we get to 500 people we can start getting together IRL\n\n@${(community as any).xHandle} @getriver_io\n\n`;

        switch (platform) {
            case "facebook":
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(waitlistUrl)}&quote=${encodeURIComponent(shareText)}`;
                break;
            case "twitter":
                shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(waitlistUrl)}`;
                break;
            case "linkedin":
                shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(waitlistUrl)}`;
                break;
            case "instagram":
                // Instagram doesn't have a direct web sharing API, we'll just copy the link
                navigator.clipboard.writeText(waitlistUrl);
                toast.success("Link copied! Share on Instagram", {
                    autoClose: 1500,
                });
                return;
            case "tiktok":
                // TikTok doesn't have a direct web sharing API, we'll just copy the link
                navigator.clipboard.writeText(waitlistUrl);
                toast.success("Link copied! Share on TikTok", {
                    autoClose: 1500,
                });
                return;
        }

        if (shareUrl) {
            window.open(shareUrl, "_blank", "noopener,noreferrer");
        }
    };

    // Use the latest data from React Query, falling back to local state for optimistic updates
    const currentWaitlistCount = waitlistCount;
    const isCurrentlyJoined = isJoined;

    // Calculate remaining people needed and progress percentage
    const remainingNeeded = 500 - currentWaitlistCount;
    const progressPercentage = Math.min(
        100,
        (currentWaitlistCount / 500) * 100,
    );

    return (
        <>
            <div className="mt-4 w-full">
                {/* Main card with absolute centering for all content */}
                <div className="mx-auto max-w-mobile rounded-md bg-v3-N500 p-4 sm:p-6 md:w-[66%]">
                    <div className="flex flex-col items-center justify-center text-center">
                        {/* Title */}
                        <div className="text-[26px] font-[500]">
                            Join the waitlist
                        </div>

                        {/* Avatar */}
                        <div className="my-2 h-[64px] w-[64px]">
                            <CommunityAvatar
                                community={{
                                    id: community.id,
                                    name: community.name,
                                    image: community.image,
                                }}
                                className="h-full w-full"
                            />
                        </div>

                        <h1 className="flex flex-col items-center justify-center text-[40px] font-[500] leading-tight">
                            {community.name}
                        </h1>

                        <p>We're building momentum for this community.</p>

                        {/* Log in/Signup buttons */}
                        {!isLoggedIn && (
                            <div className="my-4 flex space-x-2">
                                <Button
                                    variant="secondary"
                                    className="w-[160px]"
                                    onClick={() => {
                                        setIsLoginModalOpen(true);
                                        setOpenInMode("signup");
                                    }}
                                >
                                    Sign up
                                </Button>
                                <Button
                                    variant="secondary"
                                    className="w-[160px]"
                                    onClick={() => {
                                        setIsLoginModalOpen(true);
                                        setOpenInMode("login");
                                    }}
                                >
                                    Log in
                                </Button>
                            </div>
                        )}

                        {/* JOIN WAITLIST BUTTON */}
                        {isLoggedIn && !isCurrentlyJoined && (
                            <div className="my-4">
                                <Button
                                    variant="secondary"
                                    className="w-[210px] text-[15px]"
                                    onClick={handleJoinWaitlist}
                                    disabled={isPending}
                                >
                                    {isPending
                                        ? "Joining..."
                                        : "Join the waitlist"}
                                </Button>
                            </div>
                        )}

                        {/* SHARE AFTER JOINING */}
                        {isLoggedIn && isCurrentlyJoined && (
                            <div className="my-4">
                                <Button
                                    variant="secondary"
                                    className="w-[210px] text-[15px]"
                                    onClick={handleShare}
                                >
                                    Share
                                </Button>
                            </div>
                        )}

                        {/* Waitlist count */}
                        <div className="my-4 text-sm">
                            <span className="font-semibold">
                                {currentWaitlistCount}
                            </span>{" "}
                            people have joined.
                        </div>

                        {/* Progress bar - centered with flex */}
                        <div className="h-2 w-full max-w-xs overflow-hidden rounded-full bg-gray-700">
                            <div
                                className="h-full bg-blue-500 transition-all duration-300 ease-in-out"
                                style={{ width: `${progressPercentage}%` }}
                            />
                        </div>

                        {/* Remaining count text */}
                        <div className="mt-1 text-xs text-gray-400">
                            {remainingNeeded} more needed to launch events.
                        </div>

                        {/* Success message */}
                        {isLoggedIn && isCurrentlyJoined && (
                            <div className="mt-4">
                                <div className="rounded-md bg-green-900/30 px-4 py-2 text-green-500">
                                    You're on the waitlist! We'll notify you
                                    when events launch.
                                </div>
                            </div>
                        )}

                        {/* ABOUT section */}
                        <div className="mt-6 text-[24px] font-[500]">About</div>

                        {/* Description - centered with the parent flex-col items-center */}
                        <p className="max-w-[500px] px-4 text-center font-[Faktum] text-[14px] font-normal leading-[24px] text-white">
                            {community.description}
                        </p>
                    </div>
                </div>
            </div>

            {/* Log in Modal */}
            <LoginModal
                isOpen={isLoginModalOpen}
                setIsOpen={(isOpen) => {
                    setIsLoginModalOpen(isOpen);
                    // When modal closes and user is now logged in, check if we should auto-join
                    if (!isOpen && isLoggedIn && !isCurrentlyJoined) {
                        handleJoinWaitlist();
                    }
                }}
                description="Join the waitlist for this community"
                callbackUrl={`/beta/${community.slug}`}
                openInMode={openInMode}
            />

            {/* Share Modal */}
            <Modal open={isShareModalOpen} onOpenChange={setIsShareModalOpen}>
                {/* Modal content - using flex-col with items-center for perfect centering */}
                <div className="flex flex-col items-center text-white">
                    <h2 className="mb-1 text-center text-xl font-semibold">
                        You're on the list!
                    </h2>
                    <p className="mb-6 text-center text-sm">
                        We'll let you know when {community.name} is ready for
                        events.
                    </p>
                    <p className="mb-4 text-center text-sm font-medium text-v3-N300">
                        Tell your friends
                    </p>

                    {/* Social media icons */}
                    <div className="mb-6 flex justify-center gap-2">
                        <button
                            onClick={() => handleSocialShare("tiktok")}
                            className="flex h-10 w-10 items-center justify-center rounded-full bg-black p-2"
                        >
                            <FaTiktok size={20} />
                        </button>
                        <button
                            onClick={() => handleSocialShare("linkedin")}
                            className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0077B5] p-2"
                        >
                            <FaLinkedin size={20} />
                        </button>
                        <button className="flex h-10 w-10 items-center justify-center rounded-full bg-black p-2">
                            <FaThreads size={20} />
                        </button>
                        <button
                            onClick={() => handleSocialShare("instagram")}
                            className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] p-0"
                        >
                            <FaInstagram size={20} />
                        </button>
                        <button
                            onClick={() => handleSocialShare("facebook")}
                            className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1877F2] p-2"
                        >
                            <FaFacebook size={20} />
                        </button>
                        <button
                            onClick={() => handleSocialShare("twitter")}
                            className="flex h-10 w-10 items-center justify-center rounded-full bg-black p-2"
                        >
                            <FaXTwitter size={20} />
                        </button>
                    </div>

                    {/* Copy link section - fixed alignment issues */}
                    <div className="flex w-full items-center">
                        <div className="px-3 py-2">{APP_URL}/</div>
                        <div className="flex flex-1 items-center gap-[12px] rounded-md bg-v3-N500 p-[4px] pl-2">
                            <span className="truncate">{community.slug}</span>
                            <button
                                onClick={handleCopyLink}
                                className="ml-auto px-3 py-2"
                            >
                                <LuCopy size={20} />
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default WaitlistCommunity;
